import { createFileRoute, Link } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { useMemo, lazy, Suspense } from "react";
import { CheckCircle2, Loader2, Lock, Briefcase, RefreshCw, FileText, Download, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
const MessagePopup = lazy(() =>
  import("@/components/site/MessagePopup").then((m) => ({ default: m.MessagePopup })),
);
import { Button } from "@/components/ui/button";
import { fetchPackages, fetchServices } from "@/lib/content";
import { useIsB2B } from "@/hooks/use-b2b";
import { useDownloadPackageInvoice } from "@/hooks/use-download-package-invoice";
import { InvoiceDownloadControl } from "@/components/invoice/InvoiceDownloadControl";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { breadcrumbScript } from "@/lib/breadcrumb-jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";

const PACKAGES_BULLET_ICONS = [
  { Icon: Lock, className: "bg-blue-500 text-white" },
  { Icon: Briefcase, className: "bg-purple-500 text-white" },
  { Icon: RefreshCw, className: "bg-teal-500 text-white" },
  { Icon: FileText, className: "bg-orange-500 text-white" },
  { Icon: Download, className: "bg-rose-500 text-white" },
  { Icon: MessageSquare, className: "bg-emerald-500 text-white" },
];


const packagesQueryOptions = queryOptions({
  queryKey: ["packages"],
  queryFn: () => fetchPackages(),
  staleTime: 5 * 60 * 1000,
});
const servicesQueryOptions = queryOptions({
  queryKey: ["services"],
  queryFn: () => fetchServices(),
  staleTime: 5 * 60 * 1000,
});

export const Route = createFileRoute("/packages")({
  validateSearch: (s: Record<string, unknown>) => {
    const v = typeof s.service === "string" ? s.service.trim() : "";
    return v ? { service: v } : ({} as { service?: string });
  },
  loaderDeps: ({ search }) => ({ service: (search as any).service ?? "" }),
  head: ({ loaderData }: any) => {
    const m = loaderData?.matchedService;
    if (m && m.title) {
      const name = String(m.title);
      const SHORT_TITLES: Record<string, string> = {
        "bin/vat arrear return submit": "VAT/BIN Arrear Return | Online Tax VAT",
        "company tax filing": "Company Tax Filing | Online Tax VAT",
        "e tin -registration & recovery": "E-TIN Registration & Recovery | Online Tax VAT",
        "e-tin registration & recovery": "E-TIN Registration & Recovery | Online Tax VAT",
        "erc/irc registration": "ERC/IRC Registration | Online Tax VAT",
        "fast - track vat/bin registration": "Fast VAT/BIN Registration | Online Tax VAT",
        "fast-track vat/bin registration": "Fast VAT/BIN Registration | Online Tax VAT",
        "income tax return": "Income Tax Return | Online Tax VAT",
        "passport - (e- return) -account": "Passport E-Return Account | Online Tax VAT",
        "passport (e-return) account": "Passport E-Return Account | Online Tax VAT",
        "tax -consultancy": "Tax Consultancy | Online Tax VAT",
        "tax consultancy": "Tax Consultancy | Online Tax VAT",
        "trade-license": "Trade License Support | Online Tax VAT",
        "trade license": "Trade License Support | Online Tax VAT",
        "vat-registration": "VAT Registration (BIN) | Online Tax VAT",
        "vat registration": "VAT Registration (BIN) | Online Tax VAT",
        "vat-return": "VAT Return Filing | Online Tax VAT",
        "vat return": "VAT Return Filing | Online Tax VAT",
      };
      const key = name.toLowerCase().trim();
      const slugKey = String(m.slug || "").toLowerCase().trim();
      const title = SHORT_TITLES[key] || SHORT_TITLES[slugKey] || `${name} | Online Tax VAT`;
      const isIncomeTax = /income.?tax.?return|income-tax-return/.test(slugKey) || /income tax return/.test(key);
      const isEtin = /e.?tin|tin.?registration|tin.?recovery/.test(slugKey) || /e-?tin|tin certificate|tin registration/.test(key);
      const isVat = /vat.?registration|vat-registration|vat.?return|bin/.test(slugKey) || /vat registration|vat return|bin/.test(key);
      const rawDesc = isIncomeTax
        ? "Income tax return filing service in Bangladesh with clear pricing, document review and online delivery support."
        : isEtin
        ? "E-TIN registration and TIN certificate assistance in Bangladesh with secure document handling and online delivery."
        : isVat
        ? "VAT registration and VAT return filing support in Bangladesh with clear pricing and secure online service handling."
        : (m.description && String(m.description).trim())
        ? String(m.description).trim()
        : `Get ${name} service in Bangladesh online with transparent pricing, fast processing and expert support.`;
      const description = rawDesc.length > 158 ? rawDesc.slice(0, 155) + "…" : rawDesc;
      const finalTitle = isIncomeTax
        ? "Income Tax Return Filing | Online Tax VAT"
        : isEtin
        ? "E-TIN Registration | Online Tax VAT"
        : isVat
        ? "VAT Registration & Return | Online Tax VAT"
        : title;

      return {
        meta: [
          { title: finalTitle },
          { name: "description", content: description },
          { property: "og:title", content: finalTitle },
          { property: "og:description", content: description },
          { property: "og:url", content: `https://onlinetaxvat.com/packages?service=${m.slug}` },
          { property: "og:type", content: "product" },
        ],

        links: [
          { rel: "canonical", href: "https://onlinetaxvat.com/packages" },
        ],
        scripts: [breadcrumbScript([
          { name: "Home", path: "/" },
          { name: "Packages", path: "/packages" },
          { name, path: `/packages?service=${m.slug}` },
        ]), webPageScript({
          path: `/packages?service=${m.slug}`,
          name: `${name} — Pricing & Process | Online Tax VAT Bangladesh`,
          description: `Package, pricing, required documents and delivery details for ${name} in Bangladesh.`,
        })],
      };
    }
    return {
      meta: [
        { title: "Packages & Pricing | Online Tax VAT Bangladesh" },
        { name: "description", content: "View transparent BDT pricing for income tax return filing, VAT registration, VAT returns, TIN services and trade license support." },
        { property: "og:title", content: "Packages & Pricing | Online Tax VAT Bangladesh" },
        { property: "og:description", content: "View transparent BDT pricing for tax, VAT, TIN and trade license services." },
        { property: "og:url", content: "https://onlinetaxvat.com/packages" },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Packages & Pricing | Online Tax VAT Bangladesh" },
        { name: "twitter:description", content: "View transparent BDT pricing for tax, VAT, TIN and trade license services." },
      ],
      links: [
        { rel: "canonical", href: "https://onlinetaxvat.com/packages" },
      ],
      scripts: [breadcrumbScript([
        { name: "Home", path: "/" },
        { name: "Packages", path: "/packages" },
      ]), webPageScript({
        path: "/packages",
        name: "Packages — Online Tax VAT Bangladesh",
        description: "All-in-one package catalogue for online tax and VAT services in Bangladesh: VAT registration (BIN), monthly VAT return (Mushak 9.1), VAT arrear return, e-TIN registration and recovery, company tax filing, income tax return, trade license (DSCC, DNCC, other city corporations), ERC/IRC import-export registration, FAST-Track VAT/BIN registration, visa tax file packages, and ongoing tax consultancy — each with transparent BDT pricing, scope, turnaround and digital delivery.",
      })],
    };
  },
  loader: async ({ context, deps }) => {
    const [, services] = await Promise.all([
      context.queryClient.ensureQueryData(packagesQueryOptions),
      context.queryClient.ensureQueryData(servicesQueryOptions),
    ]);
    const matchedService = deps.service
      ? ((services as any[]) ?? []).find((s: any) => s.slug === deps.service) ?? null
      : null;
    return { matchedService: matchedService ? {
      slug: matchedService.slug,
      title: matchedService.title,
      title_bn: matchedService.title_bn ?? null,
      description: matchedService.description ?? null,
    } : null };
  },
  component: PackagesPage,
  pendingComponent: () => (
    <SiteShell>
      <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
    </SiteShell>
  ),
});

function PackagesPage() {
  const { t, i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
  const { service: serviceFilter = "" } = Route.useSearch();
  const { isB2B } = useIsB2B();
  const { data: packages } = useSuspenseQuery(packagesQueryOptions);
  const { data: services } = useSuspenseQuery(servicesQueryOptions);
  const loading = false;
  const { download, busyId } = useDownloadPackageInvoice();

  const filtered = useMemo(() => {
    if (!serviceFilter) return packages;
    return packages.filter((p: any) => p.services?.slug === serviceFilter);
  }, [packages, serviceFilter]);

  const activePopup = useMemo(() => {
    const popupSvc = serviceFilter
      ? services.find((s: any) => s.slug === serviceFilter && s.popup_active && s.popup_message)
      : null;
    return popupSvc
      ? { key: `svc_${popupSvc.id}`, title: popupSvc.popup_title, message: popupSvc.popup_message, duration: popupSvc.popup_duration ?? 7 }
      : null;
  }, [services, serviceFilter]);

  return (
    <SiteShell>
      {activePopup && (
        <Suspense fallback={null}>
          <MessagePopup
            storageKey={activePopup.key}
            title={activePopup.title}
            message={activePopup.message ?? ""}
            durationSeconds={activePopup.duration}
          />
        </Suspense>
      )}

      <section className="pt-14 md:pt-20 pb-20">
        <div className="container-page">
          <h1 className="sr-only">Tax, VAT & Compliance Service Packages in Bangladesh — Transparent Pricing</h1>
          <SectionHeading
            eyebrow={t("packagesPage.eyebrow")}
            title={<>{t("packagesPage.titleA")}<span className="gradient-text">{t("packagesPage.titleB")}</span></>}
            description={t("packagesPage.desc")}
          />







          {services.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2 justify-center">
              <Link to="/packages" className={`px-4 py-1.5 rounded-full text-xs font-medium border ${!serviceFilter ? "gradient-primary text-primary-foreground border-transparent" : "glass"}`}>{t("packagesPage.all")}</Link>
              {services.map((s) => (
                <Link key={s.id} to="/packages" search={{ service: s.slug } as any}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium border ${serviceFilter === s.slug ? "gradient-primary text-primary-foreground border-transparent" : "glass"}`}>{isBn && s.title_bn ? s.title_bn : s.title}</Link>
              ))}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <p className="text-center py-20 text-muted-foreground">{t("packagesPage.noResults")}</p>
          ) : (
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5">
              {filtered.map((p: any) => {
                const nameAll = `${p.name || ""} ${p.name_bn || ""}`.toLowerCase();
                const isPremium = /premium|প্রিমিয়াম/i.test(nameAll);
                const cardClass = p.popular
                  ? "ring-[3px] ring-emerald-700 dark:ring-emerald-500 shadow-[0_18px_50px_-12px_rgba(5,150,105,0.45)] bg-gradient-to-b from-emerald-50/90 to-white dark:from-emerald-900/30 dark:to-background"
                  : isPremium
                  ? "border border-amber-300/60 dark:border-amber-400/30 shadow-[0_18px_50px_-18px_rgba(217,119,6,0.45)] bg-gradient-to-br from-amber-50 via-white to-amber-100/60 dark:from-amber-500/10 dark:via-background dark:to-amber-400/10 relative overflow-hidden before:content-[''] before:absolute before:inset-0 before:bg-[linear-gradient(115deg,transparent_30%,rgba(255,255,255,0.55)_50%,transparent_70%)] before:pointer-events-none dark:before:bg-[linear-gradient(115deg,transparent_30%,rgba(255,255,255,0.08)_50%,transparent_70%)]"
                  : "glass";
                return (
                <div key={p.id}
                  className={`relative rounded-3xl p-6 flex flex-col shadow-card ${cardClass}`}>
                  {p.popular && (
                    <span className="popular-badge">
                      <span aria-hidden className="text-base drop-shadow-[0_1px_0_rgba(255,255,255,0.6)]">★</span>
                      {t("packagesPage.mostPopular")}
                    </span>
                  )}
                  {isPremium && !p.popular && (
                    <span className="absolute -top-3 right-4 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-500 to-yellow-400 text-amber-950 px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider shadow-md ring-2 ring-white dark:ring-background">
                      ✦ {isBn ? "প্রিমিয়াম" : "Premium"}
                    </span>
                  )}
                  <p className="relative z-10 text-xs uppercase tracking-wider text-muted-foreground">{isBn && p.services?.title_bn ? p.services.title_bn : p.services?.title}</p>
                  <h3 className="relative z-10 font-display font-bold text-xl mt-1">{isBn && p.name_bn ? p.name_bn : p.name}</h3>
                  {(isBn && p.description_bn) || p.description ? <p className="text-xs text-muted-foreground mt-1">{isBn && p.description_bn ? p.description_bn : p.description}</p> : null}
                  {(() => {
                    const fmt = (raw: any) => { const n = Number(raw); return Number.isFinite(n) && String(raw).trim() !== "" ? `৳ ${n.toLocaleString()}` : String(raw ?? ""); };
                    if (isB2B && p.b2b_price) {
                      const priceN = Number(p.price);
                      const b2bN = Number(p.b2b_price);
                      const savings = Number.isFinite(priceN) && Number.isFinite(b2bN) && priceN > b2bN ? priceN - b2bN : 0;
                      return (
                        <div className="mt-4 space-y-2 min-w-0">
                          <div className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 max-w-full rounded-md bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 px-2.5 py-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">{t("packagesPage.regular")}</span>
                            <span className="text-sm font-semibold text-red-700 dark:text-red-300 line-through decoration-2 whitespace-nowrap">{fmt(p.price)}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400">{t("packagesPage.b2bPrice")}</p>
                            <p className="price-display text-emerald-600 dark:text-emerald-400">{fmt(p.b2b_price)}</p>
                          </div>
                          {savings > 0 && (
                            <div className="min-w-0">
                              <span className="inline-flex flex-wrap items-center gap-1 max-w-full rounded-2xl bg-emerald-100 dark:bg-emerald-500/15 border border-emerald-300 dark:border-emerald-500/40 px-2.5 py-1 text-[11px] font-bold text-emerald-700 dark:text-emerald-300 break-words">
                                {t("packagesPage.b2bSavings", { amount: savings.toLocaleString() })}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    }
                    return <p className="price-display gradient-text mt-4">{fmt(p.price)}</p>;
                  })()}
                  <p lang="bn" className="font-bn-readable relative z-10 mt-2 text-sm text-muted-foreground" style={{ fontWeight: 600 }}>{t("packagesPage.delivery")} <span className="font-semibold text-foreground">{(isBn && p.delivery_days_bn) ? p.delivery_days_bn : (p.delivery_days || t("packagesPage.defaultDelivery"))}</span></p>
                  <ul className="relative z-10 mt-5 space-y-2.5 text-sm flex-1">
                    {((isBn && Array.isArray(p.features_bn) && p.features_bn.length > 0 ? p.features_bn : p.features) ?? []).map((f: string, k: number) => (
                      <li key={k} className="flex items-start gap-2">
                        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 mt-0.5">
                          <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Button asChild className={`relative z-10 mt-6 ${p.popular ? "popular-btn shadow-glow" : ""}`} variant={p.popular ? "default" : "outline"}>
                    <Link to="/upload" search={{ service: p.services?.slug ?? "", package: p.slug, packageId: p.id } as any}>{t("packagesPage.getStarted")}</Link>
                  </Button>
                  <div className="relative z-10 mt-2">
                    <InvoiceDownloadControl
                      busy={busyId === p.id}
                      label={t("packagesPage.downloadInvoice")}
                      onDownload={(opts) => download({
                        id: p.id,
                        name: p.name,
                        source: "packages",
                        price: p.price,
                        currency: p.currency,
                        document_checklist: p.document_checklist,
                        service_slug: p.services?.slug ?? null,
                      }, opts)}
                    />
                  </div>

                </div>
                );
              })}
            </div>
          )}
          {isBn ? (
            <div lang="bn" className="font-bn-readable mt-16 max-w-3xl mx-auto text-foreground">
              <h2 className="text-3xl font-bold mb-4">স্বচ্ছ প্যাকেজ প্রাইসিং — কোনো গোপন চার্জ নেই</h2>
              <p className="leading-relaxed text-muted-foreground">
                নিচের প্রতিটি প্যাকেজ বাংলাদেশের প্রকৃত করদাতাদের কথা মাথায় রেখে ডিজাইন করা হয়েছে — চাকুরিজীবী ও ফ্রিল্যান্সার থেকে শুরু করে ক্ষুদ্র ও মাঝারি শিল্প (SMEs), ই-কমার্স বিক্রেতা, আমদানিকারক এবং প্রাইভেট লিমিটেড কোম্পানি। সকল মূল্য বাংলাদেশি টাকায় (BDT) প্রদর্শিত এবং চেকআউটের সময় লকড থাকে। আমাদের এই সেবা সম্পূর্ণ প্রসেস কভার করে: ডকুমেন্ট পর্যালোচনা, নিখুঁত কর গণনা, অফিশিয়াল NBR e-Return বা IVAS পোর্টালে ই-ফাইলিং এবং আপনার কাস্টমার ড্যাশবোর্ডে প্রাপ্তি স্বীকারপত্র (Acknowledgement Slip) প্রদান। আমাদের এখানে কোনো গোপন সার্ভিস ফি নেই। যেকোনো সরকারি ট্যাক্স, ভ্যাট বা ঐচ্ছিক ১% NBR চ্যানেল ফি (শুধুমাত্র যখন আমরা আপনার পক্ষে পেমেন্ট করি) পেমেন্ট নিশ্চিত করার আগে সম্পূর্ণ আলাদা এবং স্বচ্ছভাবে দেখানো হয়।
              </p>

              <h2 className="text-xl font-semibold mt-4 mb-2">কর্পোরেট ক্লায়েন্ট এবং বিটুবি (B2B) সেবা</h2>
              <p className="leading-relaxed text-muted-foreground">
                কর্পোরেট ক্লায়েন্ট, ট্যাক্স কনসালট্যান্ট এবং এজেন্সিগুলো প্রতি মাসের ভ্যাট, উইথহোল্ডিং টিডিএস (TDS), পে-রোল কমপ্লায়েন্স এবং বার্ষিক কোম্পানি ফাইলিংয়ের জন্য B2B / VIP রেটের অনুরোধ করতে পারেন — আপনার অ্যাকাউন্ট অনুমোদিত হওয়ার সাথে সাথে এই রেটগুলো স্বয়ংক্রিয়ভাবে প্রদর্শিত হবে। প্রতিটি ফাইলিং এনবিআর-প্রত্যয়িত আয়কর আইনজীবী এবং চার্টার্ড অ্যাকাউন্ট্যান্ট দ্বারা প্রস্তুত করা হয়, যা সর্বশেষ অর্থ আইন এবং SRO-এর সাথে মিলিয়ে চেক করা হয়। এছাড়া প্রতিটি প্যাকেজের সাথে থাকছে ৩০ দিনের ফ্রি পোস্ট-ফাইলিং কোয়েরি সাপোর্ট — যা ব্যক্তি ও ব্যবসা উভয়ের জন্যই শতভাগ এনবিআর কমপ্লায়েন্স নিশ্চিত করে।
              </p>

              <h2 className="text-xl font-semibold mt-4 mb-2">আমাদের প্যাকেজের মূল সুবিধাসমূহ:</h2>
              <ul className="space-y-3">
                {[
                  { label: "লকড প্যাকেজ মূল্য:", text: "কাজ শুরু করার পর কোনো মূল্য পরিবর্তন হবে না" },
                  { label: "B2B / VIP ক্লায়েন্ট সুবিধা:", text: "চুক্তিভিত্তিক বা বিশেষ রেট স্বয়ংক্রিয়ভাবে দেখার সুবিধা" },
                  { label: "ফ্রি রি-সাবমিশন:", text: "ছোটখাটো সংশোধনের জন্য এনবিআর ফাইল ফেরত দিলে সম্পূর্ণ ফ্রিতে আবার সাবমিট করার সুবিধা" },
                  { label: "অফিশিয়াল মানি রিসিট:", text: "প্রতিটি পেমেন্টের জন্য মূসক ৬.৩ (Mushak 6.3) কমপ্লায়েন্ট অফিশিয়াল রিসিট" },
                  { label: "দ্বিভাষিক ইনভয়েস:", text: "পিডিএফ (PDF) হিসেবে ডাউনলোডযোগ্য বাংলা ও ইংরেজি ইনভয়েস" },
                  { label: "সরাসরি হোয়াটসঅ্যাপ সাপোর্ট:", text: "আপনার জন্য নির্ধারিত ডেডিকেটেড কেস অফিসারের কাছ থেকে সরাসরি সহায়তা" },
                ].map((item, i) => {
                  const { Icon, className } = PACKAGES_BULLET_ICONS[i];
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`${className} p-1.5 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon className="h-4 w-4" strokeWidth={2.25} />
                      </span>
                      <span className="pt-1"><strong className="text-foreground">{item.label}</strong> {item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="mt-16 max-w-3xl mx-auto text-center text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
              <p>
                Every package below is designed for real taxpayers in Bangladesh — from salaried individuals and
                freelancers to SMEs, e-commerce sellers, importers and private limited companies. Prices are shown in
                BDT, locked at checkout, and cover the complete workflow: document review, computation, e-filing on the
                official NBR e-Return or IVAS portal, and the acknowledgement slip in your dashboard. There are no
                hidden service fees. Any government tax, VAT or the optional 1% NBR channel fee (only when we pay on
                your behalf) is shown separately and transparently before you confirm payment.
              </p>
              <p>
                Corporate clients, tax consultants and agencies can request B2B / VIP rates for recurring monthly VAT,
                withholding TDS, payroll compliance and annual company filings — these appear automatically once your
                account is approved. Every filing is prepared by NBR-certified income tax practitioners and chartered
                accountants, checked against the latest Finance Act and SROs, and covered by 30 days of free
                post-filing query support — so full NBR compliance is guaranteed for individuals and businesses alike.
              </p>
            </div>
          )}


          {(() => {
            const key = String(serviceFilter || "").toLowerCase();
            const isVatReturn = /vat.?return|vat-annual-return|mushak|bin.?arrear/.test(key);
            const isCompany = /company.?tax|company-tax-filing|corporate.?tax/.test(key);
            const isIncomeTax = /income.?tax.?return|income-tax-return|income tax return/.test(key);
            if (isVatReturn) {
              return (
                <section aria-label="About VAT Return Filing" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">VAT Return Submission in Bangladesh — Why Timing Matters</h2>
                  <p>
                    Under the VAT and Supplementary Duty Act 2012, every business holding a BIN must submit its
                    monthly VAT return (<strong>Mushak 9.1</strong>) to the National Board of Revenue by the
                    <strong> 15th of the following month</strong> — and its <strong>annual VAT return</strong>
                    within the timeline notified by NBR. Missing even a single month triggers a BDT 10,000 fine, 2%
                    monthly interest on unpaid VAT, and — if defaults continue — suspension of your BIN, which
                    freezes invoicing, import clearance and bank operations. Timely, correctly-reconciled returns
                    also protect your input VAT rebate, keep your business audit-ready, and are increasingly checked
                    by banks and corporate buyers before onboarding a supplier.
                  </p>
                  <p>
                    Our VAT specialists handle the full paperwork for you: input–output reconciliation from your
                    Mushak 6.3 sales invoices and 6.1 purchase records, decreasing adjustments, withholding VAT
                    treatment, HS-code and service-code verification, and secure submission through the NBR IVAS
                    portal. Your documents move through encrypted, access-controlled storage — never over
                    unsecured email — and every filed return is delivered back to your dashboard with the official
                    acknowledgement and a bilingual (English + বাংলা) summary. Nil-return months, arrear returns
                    and revised returns are all covered, so your BIN stays fully compliant year-round.
                  </p>
                </section>
              );
            }
            if (isCompany) {
              return (
                <section aria-label="About Company Tax Filing" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">Company Tax Filing in Bangladesh — Assessment, Audit &amp; Risk</h2>
                  <p>
                    Corporate taxpayers in Bangladesh are assessed under the Income Tax Act 2023, with rates that
                    vary by entity type — publicly traded, non-listed, banks, insurance, telecom, tobacco and
                    one-person companies each have their own slab, plus minimum tax under Section 163, transfer
                    pricing rules for related-party transactions, and mandatory withholding TDS reporting. Company
                    returns must be filed by the <strong>15th day of the 7th month</strong> after year-end and,
                    for most private limited companies, must be accompanied by an <strong>audited financial
                    statement</strong> signed by an ICAB-listed chartered accountant. A weak or inconsistent
                    filing invites Section 183 assessment, disallowance of expenses, and heavy penalty orders
                    that are expensive and time-consuming to appeal.
                  </p>
                  <p>
                    That is why company tax filings need expert eyes — not template software. Our chartered
                    accountants and income tax practitioners prepare your return with a full expense allowability
                    review, depreciation schedule under the Third Schedule, correct treatment of provisions,
                    perquisites, foreign income and related-party transactions, and matching of withholding tax
                    certificates to your ledger. Where useful, we defend positions with proper documentation,
                    file the Director&rsquo;s certificate, transfer pricing statement (Form-TPS) and audit report,
                    and proactively engage the Deputy Commissioner of Taxes on your behalf. The goal is simple:
                    minimise assessment risk, protect legitimate deductions, and keep your company audit-ready
                    long after the return is submitted.
                  </p>
                </section>
              );
            }
            const isEtin = /e.?tin|tin.?registration|tin.?recovery/.test(key);
            if (isEtin) {
              return (
                <section aria-label="About E-TIN Registration & Recovery" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">E-TIN Registration &amp; TIN Certificate Recovery in Bangladesh</h2>
                  <p>
                    A <strong>Taxpayer&rsquo;s Identification Number (e-TIN)</strong> issued by the National Board of
                    Revenue is now the single most important tax identifier for any individual, sole-proprietor or
                    company in Bangladesh. You cannot legally open a business bank account, apply for a
                    <strong> trade licence</strong>, register land or an apartment, buy a car above 1500cc, obtain a
                    credit card, sign a large tender, join a chamber of commerce, or even renew a professional
                    membership without a valid TIN certificate. Salaried employees earning above the tax-free
                    threshold, importers, exporters, freelancers receiving remittance and directors of private
                    limited companies are all mandatorily required to hold one — and to keep it linked to a live
                    e-Return account.
                  </p>
                  <p>
                    Our team registers your e-TIN through the official NBR portal using verified NID / passport data,
                    guides jurisdiction (Circle &amp; Zone) selection to match your address and income category, and
                    delivers the signed TIN certificate securely to your dashboard — no plaintext email, no shared
                    drives. If you have <strong>lost your TIN certificate</strong>, forgotten the login, or your
                    old TIN is stuck under an inactive Circle, we handle full <strong>TIN recovery</strong>:
                    identity re-verification, password reset, jurisdiction transfer where needed, and re-issue of a
                    fresh certified copy, so you stay fully compliant without repeating the whole registration.
                  </p>
                </section>
              );
            }
            const isIrcErc = /irc|erc|import.?registration|export.?registration/.test(key);
            if (isIrcErc) {
              return (
                <section aria-label="About IRC / ERC Registration" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">IRC &amp; ERC Registration in Bangladesh — Import &amp; Export Licensing</h2>
                  <p>
                    Every business that imports goods into Bangladesh must hold a valid <strong>Import Registration
                    Certificate (IRC)</strong>, and every business that ships goods abroad must hold an
                    <strong> Export Registration Certificate (ERC)</strong>, both issued by the Office of the
                    <strong> Chief Controller of Imports &amp; Exports (CCI&amp;E)</strong> under the Ministry of
                    Commerce. IRC/ERC eligibility is category-based — the fee slab depends on your annual import
                    ceiling — and applicants must first hold the standard prerequisites: a live
                    <strong> Trade Licence</strong>, an <strong>e-TIN</strong>, an active <strong>BIN (VAT)</strong>
                    registration, a bank solvency certificate, and current membership of a recognised
                    <strong> Chamber of Commerce or trade association</strong> such as DCCI, MCCI, FBCCI or the
                    relevant sectoral chamber.
                  </p>
                  <p>
                    Our consultants prepare the full CCI&amp;E application, arrange chamber membership where you
                    do not already have one, structure the treasury challan for the correct import/export category,
                    and coordinate with your bank for the required nominee and solvency documents. We track the
                    file through CCI&amp;E, respond to any query from the licensing officer, and hand back a scanned
                    IRC/ERC ready for LC opening and customs clearance — typically in a fraction of the time it
                    takes when a business tries to navigate CCI&amp;E, NBR, the chamber and its bank in parallel on
                    its own.
                  </p>
                </section>
              );
            }
            const isVatBin = /vat.?registration|vat-registration|bin|trade.?licen[cs]e/.test(key);
            if (isVatBin) {
              return (
                <section aria-label="About VAT Registration (BIN)" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">VAT Registration (BIN) &amp; Ongoing Compliance in Bangladesh</h2>
                  <p>
                    Under the VAT and Supplementary Duty Act 2012, any business whose annual turnover exceeds
                    <strong> BDT 3 crore</strong> — and every importer, exporter, supplier to government, VAT
                    withholding entity or business trading VAT-able goods and services regardless of turnover —
                    must obtain a 13-digit <strong>Business Identification Number (BIN)</strong> from the National
                    Board of Revenue. Turnover Tax enlistment (Mushak 2.1) applies between BDT 50 lakh and 3 crore.
                    Registration is completed entirely on the <strong>NBR VAT Online (IVAS) portal</strong>, using
                    a valid Trade Licence, e-TIN, bank statement, rental agreement or ownership deed, and the
                    signatory&rsquo;s NID.
                  </p>
                  <p>
                    Once a BIN is issued the business enters a strict monthly compliance cycle:
                    <strong> Mushak 9.1</strong> return by the 15th of the following month, proper issue of
                    <strong> Mushak 6.3</strong> tax invoices, maintenance of purchase (6.1) and sales books,
                    correct withholding-VAT treatment on procurement, and — for many sectors — quarterly and annual
                    filings alongside the monthly return. We register your BIN on IVAS, configure your VAT
                    categories and HS/service codes correctly at the start, and can continue as your outsourced VAT
                    team so returns, rebate claims and audit responses stay clean month after month.
                  </p>
                </section>
              );
            }
            if (isIncomeTax) {
              return (
                <section aria-label="About Income Tax Return Filing" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">Individual Income Tax Return Filing in Bangladesh</h2>
                  <p>
                    Individual income tax in Bangladesh is charged in progressive slabs under the Income Tax Act
                    2023. For general taxpayers in assessment year 2026-27 the first
                    <strong> BDT 4,00,000</strong> is tax-free, followed by 5%, 10%, 15%, 20%, 25% and a top rate
                    of <strong>30%</strong>, with enhanced tax-free ceilings for women, senior citizens (65+),
                    persons with disability, gazetted freedom fighters and third-gender taxpayers. Every eligible
                    individual — salaried employees above the threshold, business owners, professionals,
                    freelancers, non-resident Bangladeshis with local income, TIN holders required to file a
                    return, and anyone claiming a refund — must file by <strong>Tax Day (30 November)</strong>.
                    Late filing triggers a <strong>2% delay interest</strong> on tax due, penalty under Section 266,
                    loss of investment rebate on many instruments, and loss of the return-filing benefit
                    (PSR / Proof of Submission of Return) that banks, city corporations and buyers now routinely
                    demand.
                  </p>
                  <p>
                    Our structured process removes that risk. A dedicated income-tax practitioner reviews your
                    salary certificate, bank statements, house-property income, capital gains, business/profession
                    accounts and eligible investments (DPS, life insurance, provident fund, sanchayapatra,
                    listed shares), computes rebate under the Sixth Schedule, prepares a full statement of
                    <strong> assets, liabilities &amp; lifestyle (IT-10B / IT-10BB)</strong> where required, and
                    e-files the return on the NBR e-Return portal. You receive the acknowledgement slip and PSR
                    directly in your dashboard, along with a bilingual working sheet so future assessments,
                    bank loans and visa applications are backed by clean, defensible records.
                  </p>
                </section>
              );
            }
            const isPassport = /passport|e.?return.?account|non.?resident/.test(key);
            if (isPassport) {
              return (
                <section aria-label="About Passport Holder e-Return Account" className="mt-8 max-w-3xl mx-auto text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <h2 className="font-display font-bold text-xl md:text-2xl text-foreground">Passport Holder e-Return Account Setup — NBR Portal Access for Residents &amp; NRBs</h2>
                  <p>
                    The NBR <strong>e-Return portal</strong> is normally opened using a Bangladeshi mobile number
                    linked to the taxpayer&rsquo;s <strong>National ID (NID)</strong>. That leaves a large group
                    of taxpayers — <strong>non-resident Bangladeshis (NRBs)</strong>, dual nationals, foreign
                    passport holders with Bangladesh-source income, minors without NID, and residents whose NID
                    is not yet linked to a valid operator SIM — unable to self-register. For these cases NBR
                    allows account opening on a <strong>passport basis</strong>, provided the passport data,
                    photograph and (where applicable) BRC/NID reference are matched, jurisdiction is correctly
                    assigned, and the mobile number used for OTP is held in the taxpayer&rsquo;s own name.
                  </p>
                  <p>
                    We handle that end-to-end setup: verification of your passport and visa/residency status,
                    correct jurisdiction (Circle &amp; Zone) selection based on your Bangladesh address or
                    property, mapping of your TIN to the e-Return login, secure OTP handling, and first-time
                    profile configuration — including bank account, foreign-remittance and dependant details.
                    Once your passport-based e-Return account is active, you can log in from anywhere in the
                    world to file your return, download your acknowledgement, request PSR, and manage future
                    filings without needing to visit an NBR office in person.
                  </p>
                </section>
              );
            }
            return null;
          })()}

          {(() => {
            const c = SEO_CONTENT["packages"];
            if (isBn) {
              const bn = {
                title: "স্বচ্ছ প্যাকেজ প্রাইসিং — কোনো গোপন চার্জ নেই",
                intro: "নিচের প্রতিটি প্যাকেজে নির্দিষ্ট সার্ভিস ফি স্পষ্ট করে দেখানো হয়েছে। ১% এনবিআর সরকারি চ্যানেল ফি (শুধুমাত্র প্রযোজ্য ক্ষেত্রে) এবং যেকোনো ঐচ্ছিক অ্যাড-অনস পেমেন্ট স্ক্রিনে আলাদাভাবে হিসাব করা হয়, যাতে আপনি সবসময় ঠিক কীসের জন্য পে করছেন তা স্পষ্টভাবে জানতে পারেন।",
                bullets: [
                  "লকড প্যাকেজ মূল্য — কাজ শুরু করার পর কোনো মূল্য পরিবর্তন হবে না",
                  "B2B / VIP ক্লায়েন্ট সুবিধা — স্বয়ংক্রিয়ভাবে চুক্তিভিত্তিক রেট দেখার সুবিধা",
                  "ফ্রি রি-সাবমিশন — ছোটখাটো সংশোধনের জন্য এনবিআর ফাইল ফেরত দিলে ফ্রিতে আবার সাবমিট করার সুবিধা",
                  "অফিশিয়াল মানি রিসিট — প্রতিটি পেমেন্টের জন্য মূসক ৬.৩ (Mushak 6.3) কমপ্লায়েন্ট রিসিট",
                  "দ্বিভাষিক ইনভয়েস — পিডিএফ (PDF) হিসেবে ডাউনলোডযোগ্য বাংলা ও ইংরেজি ইনভয়েস",
                  "সরাসরি হোয়াটসঅ্যাপ সাপোর্ট — আপনার জন্য নির্ধারিত কেস অফিসারের কাছ থেকে সরাসরি সহায়তা",
                ],
                faqs: [
                  { q: "প্যাকেজের মূল্যের মধ্যে কী কী সুবিধা অন্তর্ভুক্ত রয়েছে?", a: "আমাদের প্রতিটি প্যাকেজে আপনার প্রয়োজনীয় ডকুমেন্ট নিখুঁতভাবে পর্যালোচনা, কর বা ভ্যাট গণনা, অফিশিয়াল এনবিআর (NBR) পোর্টালে নির্ভুল ই-ফাইলিং এবং আপনার নিজস্ব ড্যাশবোর্ডে অফিশিয়াল প্রাপ্তি স্বীকারপত্র (Acknowledgement Slip) ও ইনভয়েস প্রদান করার সম্পূর্ণ প্রসেস অন্তর্ভুক্ত থাকে।" },
                  { q: "এর বাইরে কি কোনো অতিরিক্ত সরকারি ফি রয়েছে?", a: "আপনার প্রযোজ্য মূল কর বা ভ্যাট সরাসরি সরকারি চালানের মাধ্যমে জমা দিতে হয়। এছাড়া আপনি যদি আমাদের মাধ্যমে চালান সাবমিট করান, তবে শুধুমাত্র সরকারি ১% এনবিআর গেটওয়ে চ্যানেল ফি পেমেন্ট স্ক্রিনে যুক্ত হবে, যা একদম স্বচ্ছভাবে পেমেন্টের আগেই দেখতে পাবেন।" },
                  { q: "আমি কি পরবর্তীতে আমার প্যাকেজ আপগ্রেড বা ডাউনগ্রেড করতে পারব?", a: "হ্যাঁ, কাজ শুরু করার আগে বা ফাইল প্রসেসিং চলাকালীন যেকোনো সময় আপনি আপনার ড্যাশবোর্ড থেকে অথবা আপনার ডেডিকেটেড কেস অফিসারের সাথে কথা বলে সহজেই প্যাকেজ পরিবর্তন বা আপগ্রেড করে নিতে পারবেন।" },
                  { q: "আপনারা কি প্রাতিষ্ঠানিক বা কর্পোরেট রিটেইনারশিপ সেবা প্রদান করেন?", a: "হ্যাঁ, কোম্পানিগুলোর প্রতি মাসের নিয়মিত ভ্যাট কমপ্লায়েন্স, টিডিএস (TDS) রিটার্ন, পে-রোল ম্যানেজমেন্ট এবং বার্ষিক ট্যাক্স ফাইলিংয়ের জন্য আমাদের বিশেষ B2B / VIP মান্থলি রিটেইনারশিপ প্যাকেজ রয়েছে, যা আপনি আপনার অ্যাকাউন্ট ভেরিফাইড হওয়ার পর ড্যাশবোর্ডে দেখতে পাবেন।" },
                ],
              };
              return <SeoFaqBlock title={bn.title} intro={bn.intro} bullets={bn.bullets} bulletIcons={PACKAGES_BULLET_ICONS} faqs={bn.faqs} />;
            }
            return <SeoFaqBlock title={c.title} intro={c.intro} bullets={c.bullets} bulletIcons={PACKAGES_BULLET_ICONS} faqs={c.faqs} />;
          })()}
        </div>
      </section>
      <HiddenSeoContext topic="packages" />
    </SiteShell>
  );
}
